import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Eye, EyeOff, ExternalLink, Trash2, Star } from "lucide-react";
import {
  listAllReviews,
  adminHideReview,
  adminDeleteReview,
  type AdminReviewRow,
} from "@/lib/reviews.functions";

export const Route = createFileRoute("/admin/recenze")({
  component: AdminReviewsPage,
});

type Visibility = "all" | "visible" | "hidden";
type TargetFilter = "all" | "business" | "menu_item" | "supplier";

const TARGET_LABEL: Record<string, string> = {
  business: "Podnik",
  menu_item: "Položka menu",
  supplier: "Dodavatel",
};

function AdminReviewsPage() {
  const qc = useQueryClient();
  const [visibility, setVisibility] = useState<Visibility>("all");
  const [targetType, setTargetType] = useState<TargetFilter>("all");
  const [search, setSearch] = useState("");

  const { data: rows = [], isLoading, error } = useQuery({
    queryKey: ["admin", "reviews", visibility, targetType],
    queryFn: () => listAllReviews({ data: { visibility, targetType, limit: 500 } }),
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.comment, r.author_name_snapshot, r.author_email, r.target_label]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [rows, search]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin", "reviews"] });

  const hideMut = useMutation({
    mutationFn: (v: { id: string; hidden: boolean }) => adminHideReview({ data: v }),
    onSuccess: () => { toast.success("Uloženo"); invalidate(); },
    onError: (e: Error) => toast.error(e.message),
  });

  const delMut = useMutation({
    mutationFn: (id: string) => adminDeleteReview({ data: { id } }),
    onSuccess: () => { toast.success("Recenze smazána"); invalidate(); },
    onError: (e: Error) => toast.error(e.message),
  });

  const fmt = (iso: string) => {
    try {
      return new Intl.DateTimeFormat("cs", { dateStyle: "medium", timeStyle: "short" }).format(new Date(iso));
    } catch {
      return iso.slice(0, 16).replace("T", " ");
    }
  };

  const stats = useMemo(() => {
    const visible = rows.filter((r) => !r.hidden_by_admin).length;
    return { total: rows.length, visible, hidden: rows.length - visible };
  }, [rows]);

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <div>
        <h1 className="font-serif text-2xl font-semibold text-brand-earth">Recenze</h1>
        <p className="text-sm text-muted-foreground">
          Všechna hodnocení podniků, položek menu a dodavatelů — včetně skrytých.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Tabs value={visibility} onValueChange={(v) => setVisibility(v as Visibility)}>
          <TabsList>
            <TabsTrigger value="all">Vše ({stats.total})</TabsTrigger>
            <TabsTrigger value="visible">Zveřejněné ({stats.visible})</TabsTrigger>
            <TabsTrigger value="hidden">Skryté ({stats.hidden})</TabsTrigger>
          </TabsList>
        </Tabs>
        <Tabs value={targetType} onValueChange={(v) => setTargetType(v as TargetFilter)}>
          <TabsList>
            <TabsTrigger value="all">Vše</TabsTrigger>
            <TabsTrigger value="business">Podniky</TabsTrigger>
            <TabsTrigger value="menu_item">Menu</TabsTrigger>
            <TabsTrigger value="supplier">Dodavatelé</TabsTrigger>
          </TabsList>
        </Tabs>
        <Input
          className="max-w-xs"
          placeholder="Hledat v textu, autorovi nebo cíli…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {error ? (
        <p className="text-sm text-destructive">{(error as Error).message}</p>
      ) : isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Načítám…
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-sm italic text-muted-foreground">Žádné recenze neodpovídají filtru.</p>
      ) : (
        <ul className="space-y-3">
          {filtered.map((r: AdminReviewRow) => (
            <li key={r.id}>
              <Card>
                <CardContent className="space-y-2 p-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge className="bg-brand-terracotta text-white hover:bg-brand-terracotta">
                      <Star className="mr-1 h-3 w-3 fill-current" /> {r.rating}/10
                    </Badge>
                    <Badge variant="outline">{TARGET_LABEL[r.target_type] ?? r.target_type}</Badge>
                    {r.hidden_by_admin ? (
                      <Badge variant="destructive">Skryto</Badge>
                    ) : r.target_public ? (
                      <Badge variant="secondary">Viditelné veřejně</Badge>
                    ) : (
                      <Badge variant="outline">Cíl není publikovaný</Badge>
                    )}
                    {r.open_reports > 0 && (
                      <Badge variant="destructive">Nahlášeno ({r.open_reports})</Badge>
                    )}
                    <span className="text-xs text-muted-foreground">{fmt(r.created_at)}</span>
                  </div>

                  <div className="text-sm">
                    <span className="font-medium">{r.target_label ?? r.target_id.slice(0, 8)}</span>
                    {r.target_href && (
                      <a
                        href={r.target_href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-2 inline-flex items-center gap-1 text-xs text-brand-terracotta underline"
                      >
                        <ExternalLink className="h-3 w-3" /> Otevřít stránku
                      </a>
                    )}
                  </div>

                  {r.comment ? (
                    <p className="whitespace-pre-line text-sm text-foreground/90">{r.comment}</p>
                  ) : (
                    <p className="text-sm italic text-muted-foreground">Bez komentáře</p>
                  )}
                  {r.hidden_by_admin && r.hidden_reason && (
                    <p className="text-xs text-muted-foreground">Důvod skrytí: {r.hidden_reason}</p>
                  )}

                  <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                    <span className="text-xs text-muted-foreground">
                      Autor:{" "}
                      <Link
                        to="/uzivatel/$userId"
                        params={{ userId: r.user_id }}
                        className="underline"
                      >
                        {r.author_name_snapshot ?? r.author_email ?? r.user_id.slice(0, 8)}
                      </Link>
                    </span>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={hideMut.isPending}
                        onClick={() => hideMut.mutate({ id: r.id, hidden: !r.hidden_by_admin })}
                      >
                        {r.hidden_by_admin ? (
                          <><Eye className="mr-1 h-3.5 w-3.5" /> Zobrazit</>
                        ) : (
                          <><EyeOff className="mr-1 h-3.5 w-3.5" /> Skrýt</>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive"
                        disabled={delMut.isPending}
                        onClick={() => {
                          if (confirm("Opravdu trvale smazat tuto recenzi?")) delMut.mutate(r.id);
                        }}
                      >
                        <Trash2 className="mr-1 h-3.5 w-3.5" /> Smazat
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
